/* Shared cart discovery. No nested forms, automatic additions or POST retries. */
(() => {
  if (customElements.get('ordessa-cart-addons')) return;

  let adding = false;
  let syncController;
  let syncVersion = 0;
  const origin = 'ordessa-cart-addons';
  const cartUrl = () => window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;

  document.addEventListener('ordessa:cart-mutation-start', () => {
    syncController?.abort();
    ++syncVersion;
  });

  function targets() {
    const result = [];
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    if (items?.dataset.id) result.push({ section: items.dataset.id, selector: '#main-cart-items .js-contents', kind: 'items' });
    if (footer?.dataset.id) result.push({ section: footer.dataset.id, selector: '#main-cart-footer .js-contents', kind: 'footer' });
    if (document.querySelector('#CartDrawer .drawer__inner')) result.push({ section: 'cart-drawer', selector: '#CartDrawer .drawer__inner', kind: 'drawer' });
    if (document.getElementById('cart-icon-bubble')) result.push({ section: 'cart-icon-bubble', selector: '#cart-icon-bubble', source: '.shopify-section' });
    if (document.getElementById('cart-live-region-text')) result.push({ section: 'cart-live-region-text', selector: '#cart-live-region-text', source: '.shopify-section' });
    return result;
  }

  function announce(message) {
    let status = document.getElementById('OrdessaCartAddonStatus');
    if (!status) {
      status = document.createElement('div');
      status.id = 'OrdessaCartAddonStatus';
      status.className = 'visually-hidden';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      document.body.append(status);
    }
    status.textContent = message;
  }

  function renderSections(sections, specs, focusVariant) {
    // Validate every returned section before touching the current cart.
    const updates = specs.map((spec) => {
      const html = sections?.[spec.section];
      if (typeof html !== 'string') throw new Error(`Missing cart section: ${spec.section}`);
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector(spec.source || spec.selector);
      const target = document.querySelector(spec.selector);
      if (!source || !target) throw new Error(`Missing cart container: ${spec.selector}`);
      return { spec, parsed, source, target };
    });
    const drawer = document.querySelector('cart-drawer');
    const drawerActive = drawer?.classList.contains('active');
    const previousFocus = document.activeElement;
    const previousFocusId = previousFocus?.id;
    const replacingFocus = updates.some(({ target }) => target.contains(previousFocus));

    updates.forEach(({ spec, parsed, source, target }) => {
      target.innerHTML = source.innerHTML;
      if (spec.kind === 'drawer') {
        // Keep #CartDrawer and its overlay node/listener intact.
        target.classList.toggle('is-empty', source.classList.contains('is-empty'));
        drawer.classList.toggle('is-empty', parsed.querySelector('cart-drawer')?.classList.contains('is-empty') || false);
      }
      if (spec.kind === 'items') {
        const current = document.querySelector('cart-items');
        const incoming = parsed.querySelector('cart-items');
        if (current && incoming) current.classList.toggle('is-empty', incoming.classList.contains('is-empty'));
      }
      if (spec.kind === 'footer') {
        const current = document.getElementById('main-cart-footer');
        const incoming = parsed.getElementById('main-cart-footer');
        current.classList.toggle('is-empty', incoming.classList.contains('is-empty'));
        const checkout = document.getElementById('checkout');
        const incomingCheckout = parsed.getElementById('checkout');
        if (checkout && incomingCheckout) checkout.disabled = incomingCheckout.disabled;
      }
    });
    window.OrdessaCartMutations?.refreshControls();

    if (replacingFocus || focusVariant) {
      const scope = drawerActive ? drawer : document.querySelector('cart-items');
      const addedInput = focusVariant ? scope?.querySelector(`[data-quantity-variant-id="${focusVariant}"]`) : null;
      const addedLink = addedInput?.closest('.cart-item')?.querySelector('.cart-item__name');
      const remembered = previousFocusId ? document.getElementById(previousFocusId) : null;
      const focus = addedLink || remembered || scope?.querySelector('.cart-item__name, .drawer__close, a');
      if (drawerActive && typeof trapFocus === 'function') {
        trapFocus(drawer.querySelector('.drawer__inner'), focus || drawer.querySelector('.drawer__inner'));
      } else {
        focus?.focus({ preventScroll: true });
      }
    }
  }

  // Native quantity/remove refreshes only its own surface. Refresh missing surfaces
  // on /cart too, so opening the drawer never reveals stale suggestions or totals.
  async function syncOtherSurfaces(event) {
    if (event.origin === origin || event.cartData?.errors) return;
    syncController?.abort();
    const version = ++syncVersion;
    const specs = targets().filter((spec) => {
      if (event.cartData?.sections?.[spec.section]) return false;
      // Native CartItems already fetches the page's items for product-form events.
      return event.source === 'cart-items' || spec.kind !== 'items';
    });
    if (!specs.length) return;
    syncController = new AbortController();
    const url = new URL(cartUrl(), window.location.origin);
    url.searchParams.set('sections', [...new Set(specs.map((spec) => spec.section))].join(','));
    try {
      const response = await fetch(url, { signal: syncController.signal });
      if (!response.ok) throw new Error('Cart refresh failed');
      const sections = await response.json();
      if (version === syncVersion) renderSections(sections, specs);
    } catch (error) {
      if (error.name !== 'AbortError') {
        // Never retry a mutation. A full cart view is still available via its link.
        announce('Your cart changed. Open the cart page if another cart view has not updated.');
      }
    }
  }

  function beginAnalytics(variantId, element) {
    const Event = window.StandardEvents?.CartLinesUpdateEvent;
    if (!Event) return null;
    const deferred = Event.createPromise();
    element.dispatchEvent(new Event({
      action: 'add', context: 'cart',
      lines: [{ merchandiseId: variantId, quantity: 1 }], promise: deferred.promise,
    }));
    return deferred;
  }

  function resolveAnalytics(deferred) {
    if (!deferred) return;
    const Event = window.StandardEvents?.CartLinesUpdateEvent;
    const promise = typeof CartItems !== 'undefined'
      ? CartItems.fetchCartData()
      : fetch(`${cartUrl()}.json`).then((response) => response.json());
    promise.then((cart) => {
      if (!cart?.currency) throw new Error('Missing cart currency');
      deferred.resolve({ cart: Event.createCartFromAjaxResponse(cart) });
    }).catch((error) => deferred.reject(error));
  }

  class OrdessaCartAddons extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-variant-id]').forEach((button) => {
        button.hidden = false;
        button.disabled = adding;
      });
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    onClick = async (event) => {
      const button = event.target.closest('button[data-variant-id]');
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      if (adding || window.OrdessaCartMutations?.busy || document.querySelector('.cart__items--disabled')) return;
      const variantId = button.dataset.variantId;
      if (!/^\d+$/.test(variantId)) return;

      const specs = targets();
      const errorRegion = this.querySelector('.ord-cart-addons__error');
      const releaseCart = window.OrdessaCartMutations?.begin();
      if (!releaseCart) return;
      errorRegion.hidden = true;
      adding = true;
      this.setAttribute('aria-busy', 'true');
      button.textContent = 'Adding…';
      let deferred;
      let added = false;
      let confirmedFailure = false;

      try {
        deferred = beginAnalytics(variantId, this);
        const response = await fetch(window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: [...new Set(specs.map((spec) => spec.section))],
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          confirmedFailure = true;
          throw new Error(typeof data.description === 'string' ? data.description : 'This item could not be added. Please check its product page.');
        }
        added = true;
        resolveAnalytics(deferred);
        button.textContent = 'Added';
        renderSections(data.sections, specs, variantId);
        announce(`${button.dataset.productTitle} added to your cart.`);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          // Like native CartItems, we have already rendered all returned sections.
          // This source avoids duplicate native fetches; origin identifies this UI.
          Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-items', origin, cartData: data, variantId,
          })).catch(() => {});
        }
      } catch (error) {
        if (added) {
          // The add succeeded but section rendering failed. GET the full cart;
          // never repeat the POST or present a retry that could add twice.
          window.location.assign(cartUrl());
          return;
        }
        deferred?.reject(error);
        const message = confirmedFailure ? error.message : 'We could not confirm the update. Check your cart before trying again.';
        errorRegion.querySelector('span').textContent = message;
        errorRegion.hidden = false;
        const CartError = window.StandardEvents?.CartErrorEvent;
        if (CartError) this.dispatchEvent(new CartError({ error: message, code: confirmedFailure ? 'INVALID' : 'SERVICE_UNAVAILABLE' }));
      } finally {
        adding = false;
        releaseCart();
        document.querySelectorAll('ordessa-cart-addons [data-variant-id]').forEach((control) => { control.disabled = false; });
        if (this.isConnected) {
          this.removeAttribute('aria-busy');
          button.textContent = 'Add to cart';
        }
      }
    };
  }

  customElements.define('ordessa-cart-addons', OrdessaCartAddons);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, syncOtherSurfaces);
  }
})();
